import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import socket from '../socket';
import { Button, Form } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../lib/api';
import TopBar from '../components/TopBar';
import LeaderboardGlobal from '../components/LeaderboardGlobal';
import LeaderboardPersonal from '../components/LeaderboardPersonal';

const DIFFICULTIES = [
  { id: 'easy', label: 'Easy' },
  { id: 'medium', label: 'Medium' },
  { id: 'hard', label: 'Hard' },
  { id: 'expert', label: 'Expert' }
];

const Lobby = () => {
  const { user, token, isGuest, logout } = useAuth();
  const username = user?.username || '';
  const [rooms, setRooms] = useState([]);
  const [newRoomName, setNewRoomName] = useState('');
  const [challengeTarget, setChallengeTarget] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [challenges, setChallenges] = useState([]);
  const [message, setMessage] = useState('');
  const [healthOk, setHealthOk] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    socket.on('roomList', (list) => setRooms(list));
    socket.emit('getRooms');
    socket.on('roomCreated', ({ roomId }) => {
      navigate(`/game/${roomId}`);
    });
    return () => {
      socket.off('roomList');
      socket.off('roomCreated');
    };
  }, [navigate]);

  useEffect(() => {
    async function loadChallenges() {
      if (isGuest) return;
      try {
        const payload = await apiRequest('/challenges/inbox', { token });
        setChallenges(payload.challenges || []);
      } catch (_err) {
        setChallenges([]);
      }
    }
    loadChallenges();
  }, [token, isGuest]);

  useEffect(() => {
    let cancelled = false;
    async function checkHealth() {
      try {
        const payload = await apiRequest('/ping');
        if (!cancelled) setHealthOk(payload?.message === 'pong');
      } catch (_err) {
        if (!cancelled) setHealthOk(false);
      }
    }
    checkHealth();
    const timerId = setInterval(checkHealth, 60000);
    return () => {
      cancelled = true;
      clearInterval(timerId);
    };
  }, []);

  const handleCreateRoom = (event) => {
    event.preventDefault();
    if (!newRoomName.trim()) return;
    socket.emit('createRoom', { roomName: newRoomName.trim(), username });
  };

  const handleJoinRoom = (roomId) => {
    socket.emit('joinRoom', { roomId, username });
    navigate(`/game/${roomId}`);
  };

  async function createChallenge(event) {
    event.preventDefault();
    setMessage('');
    try {
      if (isGuest) {
        setMessage('Sign in with an account to create challenges.');
        return;
      }
      await apiRequest('/challenges', {
        method: 'POST',
        token,
        body: {
          opponentUsername: challengeTarget.trim() || undefined,
          difficulty
        }
      });
      setMessage('Challenge sent. It will appear below once your friend accepts.');
      setChallengeTarget('');
      const list = await apiRequest('/challenges/inbox', { token });
      setChallenges(list.challenges || []);
    } catch (err) {
      setMessage(err.message);
    }
  }

  async function acceptChallenge(challengeId) {
    try {
      await apiRequest(`/challenges/${challengeId}/accept`, { method: 'POST', token });
      const list = await apiRequest('/challenges/inbox', { token });
      setChallenges(list.challenges || []);
    } catch (err) {
      setMessage(err.message);
    }
  }

  function startSolo() {
    navigate(`/game/solo-${Date.now()}?mode=solo-ai&difficulty=${difficulty}`);
  }

  function playChallenge(challenge) {
    navigate(
      `/game/challenge-${challenge._id}?mode=challenge&seed=${challenge.seed}&difficulty=${challenge.difficulty}&challengeId=${challenge._id}`
    );
  }

  const firstName = username.split(/[\s_-]/)[0] || username;

  return (
    <div className="page-shell">
      <TopBar>
        <button type="button" className="nav-link-btn" onClick={() => navigate('/rules')}>
          Rules
        </button>
        <button type="button" className="nav-link-btn" onClick={() => navigate('/history')}>
          History
        </button>
        <button type="button" className="nav-link-btn danger" onClick={logout}>
          Sign out
        </button>
      </TopBar>

      <section className="lobby-hero">
        <h1>Good to see you, {firstName}.</h1>
        <p>Choose a table below and the deck will be waiting.</p>
      </section>

      {message && (
        <div className="surface-panel p-3 mb-3" role="status">
          {message}
        </div>
      )}

      <div className="lobby-grid">
        <div className="lobby-main">
          <section className="surface-panel dark play-card primary" aria-labelledby="solo-title">
            <div className="play-card-head">
              <span className="mini-counter counter-yellow" aria-hidden="true">
                1
              </span>
              <div>
                <h2 id="solo-title">Play vs. the Computer</h2>
                <p>A quick game against the house. Pick a difficulty and deal.</p>
              </div>
            </div>
            <div>
              <Form.Label as="div">Computer difficulty</Form.Label>
              <div className="segmented" role="radiogroup" aria-label="Computer difficulty">
                {DIFFICULTIES.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={difficulty === option.id}
                    className={`segment ${difficulty === option.id ? 'active' : ''}`}
                    onClick={() => setDifficulty(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <Button size="lg" onClick={startSolo}>
              Deal the Cards
            </Button>
          </section>

          <div className="secondary-grid">
            <section className="surface-panel play-card" aria-labelledby="live-title">
              <div className="play-card-head">
                <span className="mini-counter counter-blue" aria-hidden="true">
                  2
                </span>
                <div>
                  <h2 id="live-title">Play Live</h2>
                  <p>Open a table and friends join in real time.</p>
                </div>
              </div>
              <Form onSubmit={handleCreateRoom} className="inline-form">
                <Form.Control
                  type="text"
                  placeholder="Name your table"
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  aria-label="Table name"
                />
                <Button type="submit">Open</Button>
              </Form>
              {rooms.length === 0 ? (
                <p className="empty-hint">No open tables right now.</p>
              ) : (
                <ul className="room-list">
                  {rooms.map((room) => (
                    <li key={room.id} className="room-row">
                      <div>
                        <div className="room-row-name">{room.name}</div>
                        <div className="room-row-meta">
                          {room.numPlayers} {room.numPlayers === 1 ? 'player' : 'players'} seated
                        </div>
                      </div>
                      <Button variant="outline-dark" size="sm" onClick={() => handleJoinRoom(room.id)}>
                        Join
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="surface-panel play-card" aria-labelledby="challenge-title">
              <div className="play-card-head">
                <span className="mini-counter counter-red" aria-hidden="true">
                  3
                </span>
                <div>
                  <h2 id="challenge-title">Challenge a Friend</h2>
                  <p>Same deck, play whenever. Best score wins.</p>
                </div>
              </div>
              {isGuest ? (
                <p className="empty-hint">Sign in with an account to send and play challenges.</p>
              ) : (
                <>
                  <Form onSubmit={createChallenge} className="inline-form">
                    <Form.Control
                      type="text"
                      placeholder="Friend's username (or leave blank)"
                      value={challengeTarget}
                      onChange={(e) => setChallengeTarget(e.target.value)}
                      aria-label="Friend's username"
                    />
                    <Button type="submit">Send</Button>
                  </Form>
                  {challenges.length === 0 ? (
                    <p className="empty-hint">No challenges yet.</p>
                  ) : (
                    <ul className="challenge-list">
                      {challenges.map((challenge) => {
                        const isOpen = challenge.status === 'open';
                        const canAccept = isOpen && challenge.creatorUserId !== user?.id;
                        const canPlay = !isOpen;
                        return (
                          <li key={challenge._id} className="challenge-row">
                            <div>
                              <div className="challenge-row-name">
                                {challenge.creatorUsername} vs. {challenge.opponentUsername || 'anyone'}
                              </div>
                              <div className="challenge-row-meta">
                                {challenge.difficulty} · {challenge.status}
                              </div>
                            </div>
                            <div className="d-flex gap-2">
                              {canAccept && (
                                <Button size="sm" variant="outline-success" onClick={() => acceptChallenge(challenge._id)}>
                                  Accept
                                </Button>
                              )}
                              {canPlay && (
                                <Button size="sm" variant="outline-dark" onClick={() => playChallenge(challenge)}>
                                  Play
                                </Button>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </section>
          </div>
        </div>

        <aside className="lobby-side">
          <LeaderboardGlobal />
          <LeaderboardPersonal token={token} enabled={!isGuest} />
        </aside>
      </div>

      <div className="lobby-footer">
        <span className={`status-dot ${healthOk ? 'ok' : 'down'}`} aria-hidden="true" />
        {healthOk ? 'All tables open' : 'Connection issue — retrying'}
      </div>
    </div>
  );
};

export default Lobby;
